/**
 * Scala orizzontale dei livelli di prezzo di un'analisi (F-068).
 *
 * ## Perché una barra, come per i mercati
 *
 * Gli stessi numeri stanno già nell'elenco indicizzato sopra. Quello che
 * l'elenco non può dare è la **distanza**: quanto sta sotto lo stop, quanto
 * manca all'obiettivo, e soprattutto **da che parte** si trova il prezzo di
 * oggi rispetto ai due. È informazione posizionale, e si legge solo su un asse
 * — lo stesso ragionamento che ha portato alla linea del tempo dei mercati.
 *
 * ## L'asse non parte da zero
 *
 * Sarebbe la scelta corretta per un grafico a barre, dove l'area comunica una
 * quantità. Qui le grandezze da confrontare sono **differenze fra prezzi
 * vicini** — stop 113,45 e obiettivo 128,62 — e un asse da zero le
 * schiaccerebbe in due tacche indistinguibili. L'asse copre quindi l'intervallo
 * dei livelli più un margine, ed è etichettato ai due estremi perché non si
 * possa scambiare per una scala assoluta.
 *
 * ## Con meno di due livelli non si disegna
 *
 * Una barra con un solo punto non mostra alcuna distanza: sarebbe decorazione
 * che finge di essere informazione. In quel caso resta il solo elenco.
 */

import { formatCurrency } from '@/lib/format';
import type { PriceLevel } from './ai-analysis-api';
import { LEVEL_LABELS, LEVEL_TONE } from './analysis-price-labels';

export type PriceLadderProps = {
  levels: PriceLevel[];
  /** Quotazione attuale nell'unità principale, se nota: è il «tu sei qui». */
  referencePrice: string | null;
  currency: string | null;
  unit: 'major' | 'minor';
};

type Point = { key: string; label: string; value: number; display: string; tone: string; left: number };

export function PriceLadder({ levels, referencePrice, currency, unit }: PriceLadderProps) {
  // Si ragiona sempre sull'unità PRINCIPALE: mescolare pence e sterline sullo
  // stesso asse metterebbe la quotazione attuale fuori scala di cento volte.
  const entries = levels
    .map((level) => ({ level, value: Number(level.valueMajor) }))
    .filter((entry) => Number.isFinite(entry.value) && entry.value > 0);

  const reference = referencePrice === null ? null : Number(referencePrice);
  const hasReference = reference !== null && Number.isFinite(reference) && reference > 0;

  const values = [...entries.map((entry) => entry.value), ...(hasReference ? [reference] : [])];
  if (entries.length < 2 || values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  // Margine del 12% per lato: senza, il livello estremo finirebbe esattamente
  // sul bordo e la sua etichetta uscirebbe dal riquadro.
  const padding = span > 0 ? span * 0.12 : Math.max(max * 0.05, 1);
  const from = min - padding;
  const to = max + padding;
  const positionOf = (value: number): number => ((value - from) / (to - from)) * 100;

  const points: Point[] = entries.map((entry) => ({
    key: entry.level.kind,
    label: LEVEL_LABELS[entry.level.kind],
    value: entry.value,
    display: formatCurrency(entry.level.valueMajor, currency ?? undefined),
    tone: LEVEL_TONE[entry.level.kind],
    left: positionOf(entry.value),
  }));

  return (
    <div className="ladder">
      <div
        className="ladder__track"
        role="img"
        aria-label={
          // Senza questa frase un lettore di schermo annuncerebbe una fila di
          // div vuoti. L'elenco indicizzato sopra resta comunque la versione
          // testuale completa: la barra non è l'unico accesso al dato.
          `Livelli di prezzo${currency ? ` in ${currency}` : ''}: ${points
            .map((point) => `${point.label} ${point.display}`)
            .join(', ')}${hasReference ? `. Quotazione attuale ${formatCurrency(reference, currency ?? undefined)}` : ''}`
        }
      >
        {/* La fascia fra stop e obiettivo, quando esistono entrambi: è
            l'intervallo in cui la posizione «vive» secondo l'analisi. */}
        {points.length >= 2 && (
          <div
            className="ladder__range"
            style={{
              left: `${Math.min(...points.map((point) => point.left))}%`,
              width: `${Math.max(...points.map((point) => point.left)) - Math.min(...points.map((point) => point.left))}%`,
            }}
          />
        )}

        {hasReference && (
          <div className="ladder__now" style={{ left: `${positionOf(reference)}%` }}>
            <span className="ladder__now-label">oggi</span>
          </div>
        )}

        {points.map((point) => (
          <span
            key={point.key}
            className={`ladder__point ladder__point--${point.tone}`}
            style={{ left: `${point.left}%` }}
            title={`${point.label}: ${point.display}`}
          />
        ))}
      </div>

      <div className="ladder__labels">
        {points.map((point) => (
          <span key={point.key} className="ladder__label" style={{ left: `${point.left}%` }}>
            <span className={`ladder__label-name ladder__label-name--${point.tone}`}>{point.label}</span>
            <span className="ladder__label-value">{point.display}</span>
          </span>
        ))}
      </div>

      {unit === 'minor' && (
        <p className="ladder__note">
          Il report esprime i prezzi in centesimi di {currency}; qui sono già riportati all&apos;unità intera.
        </p>
      )}
    </div>
  );
}
